import { MCPServerIdMembers, type MCPServerId } from './types';

export const APP_NAME_MAX_LENGTH = 100;

// Keep in sync with frontend app-name constraints.
export const APP_NAME_ALLOWED_REGEX = /^[A-Za-z0-9 .,_'/()!?:;@&+-]*$/;

const CONTROL_CHAR_REGEX = /[\r\n\t]+/g;
const MULTIPLE_WHITESPACE_REGEX = /\s+/g;

export function isValidMcpServerId(
  maybeServerId: string
): maybeServerId is MCPServerId {
  // Downcast to readonly string[] rather than const to perform the includes check
  return (MCPServerIdMembers as readonly string[]).includes(maybeServerId);
}

/**
 * Lightweight normalization for display as the user types.
 * Removes control characters (newlines, tabs) that can cause layout/UX issues.
 */
export function normalizeAppNameInput(value: string): string {
  return (value ?? '').replace(CONTROL_CHAR_REGEX, ' ');
}

/**
 * Normalizes and trims an app name for persistence.
 * Collapses whitespace and removes leading/trailing spaces.
 */
export function sanitizeAppNameInput(value: string): string {
  const normalized = normalizeAppNameInput(value);
  return normalized.replace(MULTIPLE_WHITESPACE_REGEX, ' ').trim();
}

export type PromptInjectionLocalMatch = {
  isInjection: boolean;
  /** The pattern source that matched, when `isInjection` is true. */
  matchedPattern?: string;
  /** True if the original text contained common invisible/evasion characters. */
  hasEvasion: boolean;
  /** Normalized representation used for resilient matching. */
  normalized: string;
};

// Common invisible characters used for evasion (ZWSP, ZWJ, BOM, soft hyphen, etc).
export const PROMPT_INJECTION_INVISIBLE_CHARS_REGEX =
  /[\u200b-\u200f\u2060\ufeff\u00ad]/g;

// High-confidence prompt-injection patterns that are safe to enforce both
// client- and server-side.
export const PROMPT_INJECTION_PATTERNS: readonly RegExp[] = [
  // Instruction override attempts
  /\bsystem\s+override\b/i,
  /\boverride\s+system\b/i,
  /\bignore\s+instructions\b/i,
  /\breset\s+instructions\b/i,
  /ignore\s+(all\s+)?(previous|above|prior)\s+(instructions?|prompts?|rules?|guidance)/i,
  /disregard\s+(all\s+)?(previous|above|prior)\s+(instructions?|prompts?|rules?|guidance)?/i,
  /forget\s+(everything|all|what)\s+(you|i)\s+(said|told|wrote)/i,
  /do\s+not\s+follow\s+(your|the|any)\s+(original|previous|prior)/i,
  /from\s+now\s+on[,:]?\s+(ignore|disregard|forget)/i,
  /new\s+(instruction|rule|mode)\s*:/i,

  // System prompt extraction
  /(show|reveal|repeat|display|output)\s+(me\s+)?(your|the)\s*(system\s*)?(prompt|instructions?)/i,
  /what\s+(is|are)\s+(your|the)\s*(system\s*)?(prompt|instructions?)/i,
  /print\s+(your\s+)?(system\s+)?(prompt|instructions?)/i,
  /show\s+the\s+system\s+prompt/i,
  /\bdeveloper\s+message\b/i,

  // Delimiter/context manipulation
  /<\/?system>/i,
  /\[\s*inst\s*\]|\[\s*\/\s*inst\s*\]/i,
  /```\s*(system|instruction|developer)\b/i,
  /<\|?\s*(im_start|im_end|endoftext)\s*\|?>/i,
  /<<\s*sys\s*>>|<<\s*\/\s*sys\s*>>/i,
  /\[system\s*message\]/i,

  // Jailbreak markers
  /\bdan\s*mode\b/i,
  /\bdo\s+anything\s+now\b/i,
  /\b(jailbreak|bypass\s+(safety|filter|restriction|guardrail))\b/i,
  /\bunlock(ed)?\s+(mode|developer|admin)\b/i,

  // Suspicious XML tags
  /<important>/i,
  /<\/?override>/i
];

// Tokens that suggest suspicious intent; used to decide whether to ask an LLM.
// (Backend should still gate this on whether the LLM is enabled.)
export const PROMPT_INJECTION_LLM_TRIGGER_TOKENS: readonly string[] = [
  'ignore',
  'disregard',
  'forget',
  'override',
  'system',
  'prompt',
  'instruction',
  'jailbreak',
  'bypass',
  'unlock',
  'dan' // "Do Anything Now"
];

export function normalizeForPromptInjectionDetection(text: string): string {
  return text
    .normalize('NFKC')
    .replace(PROMPT_INJECTION_INVISIBLE_CHARS_REGEX, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function detectPromptInjectionLocal(
  text: string
): PromptInjectionLocalMatch {
  const normalized = normalizeForPromptInjectionDetection(text ?? '');
  const hasEvasion = PROMPT_INJECTION_INVISIBLE_CHARS_REGEX.test(text ?? '');

  if (!normalized) {
    return { isInjection: false, hasEvasion, normalized };
  }

  for (const pattern of PROMPT_INJECTION_PATTERNS) {
    if (pattern.test(text) || pattern.test(normalized)) {
      return {
        isInjection: true,
        matchedPattern: pattern.source,
        hasEvasion,
        normalized
      };
    }
  }

  return { isInjection: false, hasEvasion, normalized };
}

export function shouldUsePromptInjectionLLM(text: string): boolean {
  const normalized = normalizeForPromptInjectionDetection(text ?? '');
  if (!normalized) return false;
  return PROMPT_INJECTION_LLM_TRIGGER_TOKENS.some((token) =>
    normalized.includes(token)
  );
}
